import clsx from 'clsx';
import React from 'react';

import { BLOCK_IMAGE, BLOCK_IMAGES } from '@gitbook/doc-core';
import { DropLine } from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { toast } from '@gitbook/spine-react';

import { useDroppableWithCallback } from '../../DragAndDrop';

/**
 * An hoverable zone to drop an image on the left/right side of another image.
 */
export function BlockImageVerticalDropLine(props: {
    position: 'before' | 'after';
    onDrop: (imageIds: string[]) => void;
    editor: EditorContainer;
    className?: string;
}): React.ReactElement {
    const { position, onDrop, editor, className } = props;

    /** Handle drop of other images */
    const drop = useDroppableWithCallback({
        shouldAccept: React.useCallback((item) => {
            return 'blockType' in item && [BLOCK_IMAGES, BLOCK_IMAGE].includes(item.blockType);
        }, []),
        onDrop: (item) => {
            if ('blockType' in item) {
                switch (item.blockType) {
                    case BLOCK_IMAGE:
                        onDrop([item.id]);
                        break;
                    case BLOCK_IMAGES: {
                        const blockImagesNode = editor.value.document.getNode(item.id);
                        if (blockImagesNode?.isBlock()) {
                            const imagesIds = blockImagesNode.blockNodes
                                .map((block) => block.key)
                                .toArray();

                            onDrop(imagesIds);
                        }
                        break;
                    }
                    default:
                        toast.error('Incorrect block type', null, {
                            description: 'You can only drop images in a block of images.',
                        });
                }
            }
        },
    });

    return (
        <div
            className={clsx(
                'relative z-10 flex justify-end overflow-hidden transition-all duration-200 ease-in-out',
                drop.isOver ? 'w-full max-w-6' : 'w-0',
                className
            )}
        >
            <DropLine
                ref={drop.setNodeRef}
                dropHovered={drop.isOver}
                position={position === 'before' ? 'left' : 'right'}
            />
        </div>
    );
}
